import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { forkJoin, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { APP_INITIALIZED_KEY } from './core/constants/state-keys';
import { CompanyService } from './core/services/company/company.service';
import { ConfigService } from './core/services/config/config.service';
import { MetadataService } from './core/services/metadata/metadata.service';
import { SeoService } from './core/services/seo/seo.service';
import { ThemeService } from './core/services/theme/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [MatButton, MatIcon, MatProgressSpinner, RouterOutlet],
})
export class AppComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly companyService = inject(CompanyService);
  private readonly themeService = inject(ThemeService);
  private readonly configService = inject(ConfigService);
  private readonly metadataService = inject(MetadataService);
  private readonly seoService = inject(SeoService);
  private readonly transferState = inject(TransferState);

  readonly state = signal<{
    status: 'loading' | 'ready' | 'error';
    error?: string;
  }>(this.getInitialState());

  private getInitialState() {
    // If the server tells us it finished initialization, we start as 'ready'
    const isInitialized = this.transferState.get(APP_INITIALIZED_KEY, false);

    return {
      status: isInitialized ? ('ready' as const) : ('loading' as const),
    };
  }

  ngOnInit(): void {
    // Even if we start as ready, we should still call initializeApp
    // to setup the observables and metadata properly.
    this.initializeApp();
  }

  private initializeApp(): void {
    // If we're already ready (e.g. from TransferState), don't show the loader again
    if (this.state().status !== 'ready') {
      this.state.set({ status: 'loading' });
    }

    this.themeService.loadAvailableThemes();

    this.configService
      .loadConfig()
      .pipe(
        catchError((error) => {
          console.error('[AppComponent] Fatal error loading config:', error);
          this.state.set({
            status: 'error',
            error: 'No pudimos cargar la configuración inicial.',
          });
          return throwError(() => error);
        }),
        switchMap(() => {
          return forkJoin({
            theme: this.themeService.applyConfiguredTheme(),
            company: this.companyService.getCompany(),
          }).pipe(
            catchError((error) => {
              console.error(
                '[AppComponent] Error during company/theme initialization:',
                error,
              );
              this.state.set({
                status: 'error',
                error: 'No pudimos conectar con los servicios de la tienda.',
              });
              return throwError(() => error);
            }),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.metadataService.update();
          this.initializeGlobalSeo();
          this.state.set({ status: 'ready' });

          // On server: mark as initialized so the client starts in 'ready'
          if (!isPlatformBrowser(this.platformId)) {
            this.transferState.set(APP_INITIALIZED_KEY, true);
          }
        },
      });
  }

  retry(): void {
    this.initializeApp();
  }

  /**
   * Initializes global SEO: JSON-LD for Organization and WebSite.
   */
  private initializeGlobalSeo(): void {
    const orgSchema = this.seoService.getOrganizationSchema();
    if (orgSchema) {
      this.seoService.setJsonLd(orgSchema, 'organization');
    }

    const websiteSchema = this.seoService.getWebSiteSchema();
    if (websiteSchema) {
      this.seoService.setJsonLd(websiteSchema, 'website');
    }
  }
}
